
import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, Phone, FileText, Calendar, ChevronDown, CheckCircle, Loader } from 'lucide-react';
import { AppData, Lead, EventType } from '../types';
import { getChatbotResponse, analyzeLeadChat, triggerEmailAutomation } from '../services/geminiService';
import { pushLeadToSheet } from '../services/sheetService';

interface ChatMessage {
    role: 'user' | 'ai';
    text: string;
    action?: 'menu' | 'contact' | 'lead_form' | null;
}

// --- HELPER: Netlify Form Encoder ---
const encode = (data: any) => {
    return Object.keys(data)
        .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
        .join("&");
};

// --- SUB-COMPONENT: LEAD FORM ---
const LeadForm: React.FC<{ onSubmit: (data: any) => void; onCancel: () => void; isSubmitting: boolean }> = ({ onSubmit, onCancel, isSubmitting }) => {
    const [formData, setFormData] = useState({
        name: '',
        eventType: 'Wedding',
        date: '',
        phone: '',
        guestCount: '',
        location: ''
    });

    const today = new Date().toISOString().split('T')[0];

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.phone || !formData.name || !formData.date) {
            alert("Name, Date, and Phone are required!");
            return;
        }
        onSubmit(formData);
    };

    return (
        <div className="bg-white p-4 rounded-xl shadow-lg border border-gray-200 mt-2 mb-2 animate-fade-in-up">
            <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <Calendar size={16} className="text-indigo-600" /> Event Enquiry
            </h4>
            <form onSubmit={handleSubmit} className="space-y-3">
                <input 
                    type="text" 
                    placeholder="Your Name" 
                    className="w-full text-xs border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-indigo-500 outline-none"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    required
                    disabled={isSubmitting}
                />
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <select 
                            className="w-full text-xs border border-gray-300 rounded-lg p-2 appearance-none bg-white focus:ring-1 focus:ring-indigo-500 outline-none"
                            value={formData.eventType}
                            onChange={e => setFormData({...formData, eventType: e.target.value})}
                            disabled={isSubmitting}
                        >
                            {Object.values(EventType).map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <ChevronDown size={14} className="absolute right-2 top-2.5 text-gray-400 pointer-events-none" />
                    </div>
                    <input 
                        type="date" 
                        min={today}
                        className="flex-1 text-xs border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-indigo-500 outline-none"
                        value={formData.date}
                        onChange={e => setFormData({...formData, date: e.target.value})}
                        required
                        disabled={isSubmitting}
                    />
                </div>
                <input 
                    type="tel" 
                    placeholder="Phone Number (Important)" 
                    className="w-full text-xs border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-indigo-500 outline-none font-medium"
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})}
                    required
                    disabled={isSubmitting}
                />
                <div className="flex gap-2">
                    <input 
                        type="number" 
                        placeholder="Guests" 
                        className="w-1/3 text-xs border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-indigo-500 outline-none"
                        value={formData.guestCount}
                        onChange={e => setFormData({...formData, guestCount: e.target.value})}
                        disabled={isSubmitting}
                    />
                    <input 
                        type="text" 
                        placeholder="Location/Venue" 
                        className="flex-1 text-xs border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-indigo-500 outline-none"
                        value={formData.location}
                        onChange={e => setFormData({...formData, location: e.target.value})}
                        disabled={isSubmitting}
                    />
                </div>
                <div className="flex gap-2 mt-2">
                    <button type="button" onClick={onCancel} disabled={isSubmitting} className="flex-1 py-2 text-xs font-bold text-gray-500 hover:bg-gray-100 rounded-lg disabled:opacity-50">Cancel</button>
                    <button type="submit" disabled={isSubmitting} className="flex-1 py-2 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-md disabled:bg-indigo-400 flex items-center justify-center gap-2">
                        {isSubmitting ? <Loader size={14} className="animate-spin" /> : "Submit Enquiry"}
                    </button>
                </div>
            </form>
        </div>
    );
};

// --- MAIN COMPONENT ---
interface ChatWidgetProps {
    data: AppData;
    isOpen: boolean;
    onClose: () => void;
    isEmbed?: boolean;
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({ data, isOpen, onClose, isEmbed = false }) => {
    const [messages, setMessages] = useState<ChatMessage[]>([
        { role: 'ai', text: "Hi! Welcome to CaterMax. Need a menu or a quote?", action: null }
    ]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [activeFormIndex, setActiveFormIndex] = useState<number | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false); // New submission state
    const scrollRef = useRef<HTMLDivElement>(null);

    // Auto-scroll
    useEffect(() => {
        if(scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, isOpen, activeFormIndex]);

    const handleSend = async (textOverride?: string) => {
        const userText = textOverride || input;
        if (!userText.trim()) return;

        const newHistory: ChatMessage[] = [...messages, { role: 'user', text: userText, action: null }];
        setMessages(newHistory);
        setInput('');
        setIsTyping(true);

        try {
            const apiHistory = newHistory.map(m => ({ role: m.role, text: m.text }));
            const responseObj = await getChatbotResponse(apiHistory, userText, data.aiConfig, data.knowledgeBase);
            
            const aiMsg: ChatMessage = { 
                role: 'ai', 
                text: responseObj.text, 
                action: responseObj.action 
            };
            
            const finalHistory = [...newHistory, aiMsg];
            setMessages(finalHistory);

            if (responseObj.action === 'lead_form') {
                setActiveFormIndex(finalHistory.length - 1);
            }

            // Lead qualification simulation
            await analyzeLeadChat(apiHistory, data.aiConfig);
        } catch (error) {
            setMessages(prev => [...prev, { role: 'ai', text: "Sorry, connection error.", action: null }]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleFormSubmit = async (formData: any) => {
        setIsSubmitting(true);
        
        const summaryText = `${formData.eventType} on ${formData.date} for ${formData.guestCount || '?'} pax at ${formData.location || 'Unknown'}.`;
        const chatLog = messages.map(m => `[${m.role.toUpperCase()}]: ${m.text}`).join('\n');

        // Create the lead object
        const newLead: Lead = {
            id: 'lead_' + Date.now(),
            name: formData.name,
            phone: formData.phone,
            source: 'Website Chat',
            status: 'HOT',
            stage: 'New', 
            eventDate: formData.date,
            confidenceScore: 95,
            summary: summaryText,
            aiReasoning: "User manually submitted detailed enquiry form.",
            createdAt: new Date().toISOString(),
            chatHistory: messages.map(m => ({ role: m.role, text: m.text }))
        };

        // 1. Submit to Google Sheet (Primary Backend)
        if (data.aiConfig.googleSheetsUrl) {
            // Pass guestCount specifically
            await pushLeadToSheet(data.aiConfig.googleSheetsUrl, newLead, formData.guestCount);
        }

        // 2. Submit to Netlify Backend (Backup / Form detection)
        try {
            await fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: encode({
                    "form-name": "catermax-leads",
                    name: formData.name,
                    phone: formData.phone,
                    eventType: formData.eventType,
                    date: formData.date,
                    guestCount: formData.guestCount,
                    location: formData.location,
                    summary: summaryText,
                    chatHistory: chatLog
                })
            });
        } catch (err) {
            console.error("Netlify Sync Failed", err);
        }

        // 3. Update Local State (Immediate Feedback)
        data.addLead(newLead);
        
        // 4. Email Simulation
        await triggerEmailAutomation(newLead, data.aiConfig);

        setIsSubmitting(false);
        setActiveFormIndex(null); 
        
        setMessages(prev => [...prev, { 
            role: 'ai', 
            text: `Thanks ${formData.name}! We have received your enquiry for ${formData.date}. Our team will call you at ${formData.phone} shortly.`, 
            action: null 
        }]);
    };

    const handleClose = () => {
        if (isEmbed) {
            // Post message to parent to close the iframe
            window.parent.postMessage('catermax-close', '*');
        } else {
            onClose();
        }
    };

    if (!isOpen) return null;

    // Layout Classes
    const containerClasses = isEmbed 
        ? "absolute inset-0 w-full h-full bg-white flex flex-col font-sans" // Embed Mode: Full container
        : "fixed bottom-6 right-6 w-80 md:w-96 h-[550px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 z-[100] animate-fade-in-up font-sans"; // Default: Floating

    return (
        <div className={containerClasses}>
            <div className={`bg-indigo-600 p-4 flex justify-between items-center text-white ${isEmbed ? 'rounded-none' : ''}`}>
                <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-full relative">
                        <Bot size={20} />
                        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 border-2 border-indigo-600 rounded-full"></span>
                    </div>
                    <div>
                        <h3 className="font-bold text-sm">CaterMax Assistant</h3>
                        <p className="text-[10px] text-indigo-100 opacity-90">Replies instantly</p>
                    </div>
                </div>
                <button onClick={handleClose} className="hover:bg-white/20 p-1.5 rounded-full transition">
                    <X size={18} />
                </button>
            </div>

            <div className="flex-1 bg-gray-50 p-4 overflow-y-auto space-y-4" ref={scrollRef}>
                {messages.map((m, i) => (
                    <div key={i} className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
                        <div className={`max-w-[85%] p-3 rounded-xl text-sm shadow-sm leading-relaxed ${
                            m.role === 'user' 
                            ? 'bg-indigo-600 text-white rounded-br-none' 
                            : 'bg-white text-gray-800 border border-gray-100 rounded-bl-none'
                        }`}>
                            {m.text}
                        </div>

                        {m.role === 'ai' && m.action === 'menu' && (
                            <div className="mt-2 animate-fade-in-up">
                                <button 
                                    onClick={() => window.open('https://catermax.in/menu', '_blank')}
                                    className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold py-2 px-4 rounded-lg flex items-center gap-2 shadow-sm transition transform hover:-translate-y-0.5"
                                >
                                    <FileText size={14} /> View Full Menu
                                </button>
                            </div>
                        )}

                        {m.role === 'ai' && m.action === 'contact' && (
                            <div className="mt-2 flex gap-2 animate-fade-in-up">
                                <a 
                                    href="tel:8296660073"
                                    className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold py-2 px-4 rounded-lg flex items-center gap-2 shadow-sm transition flex-1 justify-center"
                                >
                                    <Phone size={14} /> Call
                                </a>
                                <a 
                                    href="https://wa.me/918296660073"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="bg-green-500 hover:bg-green-600 text-white text-xs font-bold py-2 px-4 rounded-lg flex items-center gap-2 shadow-sm transition flex-1 justify-center"
                                >
                                    <MessageCircle size={14} /> WhatsApp
                                </a>
                            </div>
                        )}

                        {m.role === 'ai' && m.action === 'lead_form' && activeFormIndex === i && (
                            <LeadForm onSubmit={handleFormSubmit} onCancel={() => setActiveFormIndex(null)} isSubmitting={isSubmitting} />
                        )}
                    </div>
                ))}
                
                {isTyping && (
                    <div className="flex justify-start">
                        <div className="bg-white border border-gray-100 p-3 rounded-xl rounded-bl-none shadow-sm flex gap-1 items-center">
                            <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce"></span>
                            <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce delay-75"></span>
                            <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce delay-150"></span>
                        </div>
                    </div>
                )}
            </div>

            <div className="p-3 bg-white border-t border-gray-100">
                <div className="flex gap-2">
                    <input 
                        type="text" 
                        className="flex-1 bg-gray-50 border border-gray-200 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
                        placeholder="Type a message..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                    />
                    <button 
                        onClick={() => handleSend()}
                        className="bg-indigo-600 text-white p-2.5 rounded-full hover:bg-indigo-700 transition shadow-md flex items-center justify-center"
                        disabled={isTyping}
                    >
                        <Send size={18} className={isTyping ? 'opacity-50' : ''} />
                    </button>
                </div>
            </div>
        </div>
    );
};
